import { generateRedirectUrl } from '@domain-group/javascript-utils'

import { generateUrls as generateCampaignUrls } from '@hooks/useCampaign'
import { generateUrls as generateOrderUrls, useRelations as useOrderRelations } from '@hooks/useOrder'
import { generateUrls as generateProviderAccountUrls } from '@hooks/useProviderAccount'
import { useRelations as useOrderItemRelations } from '@hooks/useOrderItem'
import useActivities from '@hooks/useActivities'
import useCurrentUser from '@hooks/useCurrentUser'
import useOrderItems from '@hooks/useOrderItems'
import useOrderItemTask, { useOrderItemTaskForm } from '@hooks/useOrderItemTask'
import useOrderItemTaskAttachments from '@hooks/useOrderItemTaskAttachments'
import useSelector from '@hooks/useSelector'

import type { CampaignModel, OrderItemTaskModel } from '@models/types'

type BuildRequiredFieldsParams = {
  isAgencyUser: boolean,
  orderItemTask: Partial<OrderItemTaskModel>,
}

const buildRequiredFields = (params: BuildRequiredFieldsParams) => {
  const { isAgencyUser, orderItemTask } = params

  if (isAgencyUser) return [{ key: 'address' }, { key: 'due_date' }, { key: 'instructions' }, { key: 'order_item_id' }]
  if (!orderItemTask?.id) return [{ key: 'due_date' }, { key: 'instructions' }]
  return []
}

const requestOptions = {
  include_activity_owner: true,
  include_artwork_page_overlays: true,
  include_artwork_page_preview: true,
  include_artwork_pages: true,
  include_artwork_public_download_link: true,
  include_order_agency: true,
  include_order_campaign: true,
  include_order_item_artwork: true,
  include_order_item_order: true,
  include_task_attachment_image: true,
  include_task_attachments: true,
  include_task_order_item: true,
}

type Options = {
  campaign?: Partial<CampaignModel>,
}

function useCreateOrEditOrderItemTaskModal(initOrderItemTask: Partial<OrderItemTaskModel> = {}, options: Options = {}) {
  const orderItemTaskPayload = useOrderItemTask(
    initOrderItemTask,
    { performHttpRequests: !!initOrderItemTask.id, requestOptions },
  )

  const { canEditTask, orderItem, orderItemTask } = orderItemTaskPayload

  // Current User
  const { isAgencyUser } = useCurrentUser()

  const customRequiredFields = buildRequiredFields({ isAgencyUser, orderItemTask })

  // Form
  const formPayload = useOrderItemTaskForm(orderItemTask, { customRequiredFields, validateOn: 'change' })
  const {
    entityState: {
      provider_employee_id,
    },
  } = formPayload

  // Attachments
  const orderItemTaskAttachmentsPayload = useOrderItemTaskAttachments(
    { filters: { order_item_task_id: orderItemTask?.id } },
  )

  // Activities
  const activitiesPayload = useActivities({
    filters: { recipient_id: orderItemTask?.id, recipient_type: 'OrderItemTask' },
    performHttpRequests: !!orderItemTask?.id,
  })

  // Load Order Items when in create mode
  const campaignId = options?.campaign?.id

  const orderItemsPayload = useOrderItems({
    filters: { campaign_id: campaignId, status_group_key: 'not_pending', taskable_only: true },
    performHttpRequests: isAgencyUser && !initOrderItemTask?.id && !!campaignId,
    requestOptions: { include_order_item_campaign_id: true, include_order_item_service: true },
  })

  const { filteredOrderItems } = orderItemsPayload

  // User
  const { campaigns, users } = useSelector(reduxState => reduxState.entities)

  const providerEmployeeUser = Object.values(users).find(user => (
    user.actable_type === 'ProviderEmployee' && user.actable_id === provider_employee_id
  ))

  const { order } = useOrderItemRelations(orderItem)
  const { agency, campaign: orderCampaign } = useOrderRelations(order)

  // @ts-expect-error TS2339
  const campaign = orderCampaign.id ? orderCampaign : campaigns[campaignId] || {}

  // URLs
  const { editCampaignUrl } = generateCampaignUrls(campaign)
  const { editOrderUrl } = generateOrderUrls(order)
  const { editProviderAccountUrl } = generateProviderAccountUrls({ agency_id: agency?.id })

  const payload = encodeURIComponent(JSON.stringify({ orderItemTask: { id: orderItemTask?.id } }))
  const redirect = generateRedirectUrl({
    append: `showModal=CreateOrEditOrderItemTaskModal&showModalPayload=${payload}`,
  })

  const isExistingOrderItemTask = !!initOrderItemTask.id

  return {
    ...orderItemTaskPayload,
    activitiesPayload,
    agency,
    campaign,
    disableEditing: isAgencyUser && isExistingOrderItemTask && !canEditTask,
    filteredOrderItems,
    formPayload,
    imageUrl: orderItemTask?.instruction_image?.large,
    isAgencyUser,
    newRecord: !initOrderItemTask.id,
    order,
    orderItem,
    orderItemTask,
    orderItemTaskAttachmentsPayload,
    providerEmployeeUser,
    urls: {
      editCampaignUrl: `${editCampaignUrl}?redirect=${redirect}`,
      editProviderAccountUrl: `${editProviderAccountUrl}?redirect=${redirect}`,
      editOrderUrl: `${editOrderUrl}&redirect=${redirect}`,
    },
    users,
  }
}

export default useCreateOrEditOrderItemTaskModal

export type useCreateOrEditOrderItemTaskModalPayload = ReturnType<typeof useCreateOrEditOrderItemTaskModal>
